import { inject, Injectable } from '@angular/core';
import { ApiDataAccess, ApiResourceLocation } from '@softline/core';
import { BookmarkCategory, BookmarkSubCategory } from '../models/bookmark';
import {
  SOFTLINE_API_BOOKMARK_ADMIN,
  SOFTLINE_API_BOOKMARK_ADMIN_CATEGORY,
  SOFTLINE_API_BOOKMARK_ADMIN_SUB_CATEGORY,
} from '../softapps-core.api';

export interface BookmarkAdminDeleteParams {
  category?: BookmarkCategory | null;
  /** Only honoured together with a category: the endpoint has no sub-category-only path. */
  subCategory?: BookmarkSubCategory | string | null;
  /** yyyy-MM-dd; deletes entries whose `updatedAt` lies before it. */
  olderThan?: string | null;
}

/** The admin endpoint spells the category as an enum constant (`LAST_VIEWED`), the
    per-user endpoints as a path segment (`last-viewed`). */
export function toAdminCategory(category: BookmarkCategory | string): string {
  return category.trim().toUpperCase().replace(/-/g, '_');
}

export function bookmarkAdminLocation(
  params: BookmarkAdminDeleteParams
): ApiResourceLocation {
  const olderThan = params.olderThan?.trim();
  const queryParams = olderThan ? { olderThan } : undefined;
  const category = params.category?.trim();
  const subCategory = params.subCategory?.trim();
  if (category && subCategory)
    return {
      path: SOFTLINE_API_BOOKMARK_ADMIN_SUB_CATEGORY,
      pathParams: { category: toAdminCategory(category), subCategory },
      queryParams,
    };
  if (category)
    return {
      path: SOFTLINE_API_BOOKMARK_ADMIN_CATEGORY,
      pathParams: { category: toAdminCategory(category) },
      queryParams,
    };
  return { path: SOFTLINE_API_BOOKMARK_ADMIN, queryParams };
}

/** The backend announces a count of deleted entries but documents the body as `{}`;
    a number or any object with a numeric count-like field is read, anything else is unknown. */
export function deletedCount(response: unknown): number | null {
  if (typeof response === 'number') return Number.isFinite(response) ? response : null;
  if (!response || typeof response !== 'object') return null;
  const record = response as Record<string, unknown>;
  for (const key of ['count', 'deleted', 'deletedCount', 'deletedEntries', 'result']) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class BookmarkAdminRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  /** Deletes the bookmarks of every user that match the params; needs ROLE_BOOKMARK_ADMIN_SERVICE. */
  deleteAll(params: BookmarkAdminDeleteParams): Promise<number | null> {
    return this.dataAccess
      .delete<unknown>(bookmarkAdminLocation(params))
      .then((response) => deletedCount(response));
  }
}
